// VENDOR
import { Cookies } from 'quasar';
import moment from 'moment';

// PERSONAL LIBS
import { time } from '@shared/lib/date.lib';
import { CookiesEnum } from '@shared/enum/cookies.enum';
import { EnvEnum } from '@shared/enum/env.enum';
import { dbGetActiveUser } from '@data/datasource/local/user.database';

interface IJwtHeader {
  alg: string;
  typ: string;
}
interface IJwtPayload {
  iat: number;
  exp: number;
  uuid: string;
  data: IJwtPayloadData;
}
interface IJwtPayloadData {
  ip: string;
}

interface ICookieParam {
  expires: string;
  path: string;
  domain: string;
  secure: boolean;
  sameSite: 'Strict' | 'Lax' | 'None' | undefined;
  httpOnly: boolean;
}

enum SameSiteEnum {
  STRICT = 'Strict',
  LAX = 'Lax',
  NONE = 'None'
}

const defaultHeader: IJwtHeader = {
  alg: '',
  typ: '',
};

const defaultPayload: IJwtPayload = {
  iat: 0,
  exp: 0,
  uuid: '',
  data: {
    ip: '',
  }
};

export default class JWT {
  public jwt_hp?: string;
  public header?: IJwtHeader;
  public payload?: IJwtPayload;

  /**
   * Constructor
   * @param jwthp
   * @returns
   */
  constructor(jwt_hp?: string) {
    this.refresh(jwt_hp)
  }

  public refresh(jwt_hp?: string) {
    if (jwt_hp == null) {
      this.jwt_hp = Cookies.has(CookiesEnum.JWT_HP)
        ? Cookies.get(CookiesEnum.JWT_HP)
        : undefined;
    } else {
      this.jwt_hp =
        jwt_hp?.indexOf('.') >= 0
          ? jwt_hp?.split('.')[0] + '.' + jwt_hp.split('.')[1]
          : undefined;
    }

    // If jwt => get header and payload
    this.header = this.jwt_hp?.includes('.')
      ? JSON.parse(atob(this.jwt_hp.split('.')[0]))
      : defaultHeader;
    this.payload = this.jwt_hp?.includes('.')
      ? JSON.parse(atob(this.jwt_hp.split('.')[1]))
      : defaultPayload;
  }

  public async refreshFromLocalDb() {
    const activeUser = await dbGetActiveUser()
    if (activeUser?.jwt_hp != null && !Cookies.has(CookiesEnum.JWT_HP)) {
      this.refresh(activeUser?.jwt_hp)
      this.setCookie()
    }
  }

  /**********************************************************************
   * Payload getters
   **********************************************************************/
  public getIp(): string | undefined {
    return this.payload?.data.ip;
  }
  public getExp(): number | undefined {
    return this.payload?.exp;
  }
  public getData(): IJwtPayloadData | undefined {
    return this.payload?.data;
  }
  public getAdvancedExp() {
    return {
      seconds: this.payload ? this.payload.exp : 0,
      milliseconds: this.payload ? this.payload.exp * 1000 : 0,
    };
  }

  /**********************************************************************
   * Advanced getters
   **********************************************************************/
  // Get expires date
  public getDateExp(): string {
    return this.payload
      ? moment(this.payload.exp * 1000).format('DD/MM/YYYY HH:mm:ss')
      : '';
  }
  // Get difference in milliseconds between expires and now
  public getRemainingTime(): number {
    return this.payload ? this.payload.exp - moment().unix() : 0;
  }

  /**********************************************************************
   * Private methods
   **********************************************************************/
  private setCookieParam(): ICookieParam {
    const params: ICookieParam = {
      expires: (this.payload?.exp ?? 0).toString() + 's', // In seconds
      path: `${process.env.FRONTEND.cookiePath ?? ''}`,
      domain: `${process.env.FRONTEND.cookieDomain ?? ''}`,
      secure: process.env.ENV !== EnvEnum.LOCAL,
      sameSite: SameSiteEnum.STRICT,
      httpOnly: false,
    };

    return params;
  }

  /**********************************************************************
   * Public methods
   **********************************************************************/
  /**
   * Set cookie
   */
  public setCookie() {
    if (!Cookies.has(CookiesEnum.JWT_HP) && this.jwt_hp != null) {
      Cookies.set(CookiesEnum.JWT_HP, this.jwt_hp, this.setCookieParam());
    }
  }

  /**
   * Check JWT validity
   * @returns boolean
   */
  public isValid(): boolean {
    return this.payload && this.header
      ? this.header.typ.toUpperCase() === 'JWT' &&
          this.payload.uuid !== '' &&
          time() <= this.payload.exp
      : false;
  }

  /**
   * Clear JWT Cookies
   */
  public clearCookies() {
    if (Cookies?.has(CookiesEnum.JWT_HP)) {
      Cookies.remove(CookiesEnum.JWT_HP, this.setCookieParam());
      this.jwt_hp = undefined;
    }
  }

  /**********************************************************************
   * Static methods
   **********************************************************************/
  /**
   *
   * @param {string} jwt_hp
   */
  public static isValid(jwt_hp?: string): boolean {
    const jwt = new JWT(jwt_hp);

    return jwt.payload && jwt.header
      ? jwt.header.typ.toUpperCase() === 'JWT' &&
          jwt.payload.uuid !== '' &&
          time() <= jwt.payload.exp
      : false;
  }

  /**
   *
   * @param {string} j
   */
  public static getPayloadFromJwtHp(j = ''): IJwtPayload {
    const _jwt_hp = j.includes('.')
      ? j.split('.')[0] + '.' + j.split('.')[1]
      : undefined;
    return _jwt_hp?.includes('.')
      ? (JSON.parse(atob(_jwt_hp.split('.')[1])) as IJwtPayload)
      : defaultPayload;
  }
}
